import axios from 'axios';
import {
  User,
  Friend,
  FriendRequest,
  FriendLocation,
  RegisterData,
  LoginData,
  AuthResponse,
  FriendGroup,
  FriendGroupWithMembers,
  CreateFriendGroupData,
  AddToGroupData,
  TemporaryLocation,
  TemporaryLocationWithFriend,
  CreateTemporaryLocationData,
  UpdateTemporaryLocationData,
  UserSearchResult,
  ContactHash,
  ContactMatch,
  ContactSyncStatus,
  FriendSuggestion,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  register: async (data: RegisterData): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/register', data);
    return response.data;
  },

  login: async (data: LoginData): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/login', data);
    return response.data;
  },

  getProfile: async (): Promise<{ user: User }> => {
    const response = await api.get<{ user: User }>('/auth/profile');
    return response.data;
  },
};

// User API (includes RGPD endpoints: export and delete account)
export const userAPI = {
  updateProfile: async (data: Partial<User>): Promise<{ message: string; user: User }> => {
    const response = await api.put<{ message: string; user: User }>('/users/profile', data);
    return response.data;
  },

  updateAddress: async (data: Partial<User['address']>): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>('/users/address', data);
    return response.data;
  },

  uploadPhoto: async (file: File): Promise<{ message: string; photo_url: string }> => {
    const formData = new FormData();
    formData.append('photo', file);

    const response = await api.post<{ message: string; photo_url: string }>(
      '/users/photo',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  deletePhoto: async (): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>('/users/photo');
    return response.data;
  },

  searchUsers: async (query: string): Promise<{ users: User[] }> => {
    const response = await api.get<{ users: User[] }>(`/users/search?q=${encodeURIComponent(query)}`);
    return response.data;
  },

  searchUsersAutocomplete: async (query: string, limit: number = 7): Promise<{ users: UserSearchResult[] }> => {
    const response = await api.get<{ users: UserSearchResult[] }>(
      `/users/search/autocomplete?q=${encodeURIComponent(query)}&limit=${limit}`
    );
    return response.data;
  },

  getUserById: async (id: number): Promise<{ user: User }> => {
    const response = await api.get<{ user: User }>(`/users/${id}`);
    return response.data;
  },

  exportUserData: async (): Promise<any> => {
    const response = await api.get<any>('/users/export');
    return response.data;
  },

  deleteAccount: async (): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>('/users/account');
    return response.data;
  },

  updateAddressPrivacy: async (data: {
    default_visibility_level?: number;
    apartment_number?: string;
    access_code?: string;
    building_name?: string;
    floor?: string;
    delivery_instructions?: string;
  }): Promise<{ message: string; address: any }> => {
    const response = await api.put<{ message: string; address: any }>('/users/address/privacy', data);
    return response.data;
  },
};

// Friends API
export const friendsAPI = {
  sendRequest: async (targetUserId: number, requestMessage?: string): Promise<{ message: string; friendship: { id: number } }> => {
    const response = await api.post<{ message: string; friendship: { id: number } }>('/friends/request', {
      friend_id: targetUserId,
      message: requestMessage || undefined,
    });
    return response.data;
  },

  updatePendingRequest: async (friendshipId: number, requestMessage?: string): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(`/friends/request/${friendshipId}`, {
      message: requestMessage || null,
    });
    return response.data;
  },

  acceptRequest: async (friendshipId: number): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(`/friends/accept/${friendshipId}`);
    return response.data;
  },

  rejectRequest: async (friendshipId: number): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(`/friends/reject/${friendshipId}`);
    return response.data;
  },

  cancelSentRequest: async (friendshipId: number): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/friends/cancel/${friendshipId}`);
    return response.data;
  },

  removeFriend: async (friendId: number): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/friends/remove/${friendId}`);
    return response.data;
  },

  getPendingRequests: async (): Promise<{ requests: FriendRequest[] }> => {
    const response = await api.get<{ requests: FriendRequest[] }>('/friends/pending');
    return response.data;
  },

  getSentRequests: async (): Promise<{ requests: FriendRequest[] }> => {
    const response = await api.get<{ requests: FriendRequest[] }>('/friends/sent');
    return response.data;
  },

  getFriends: async (): Promise<{ friends: Friend[] }> => {
    const response = await api.get<{ friends: Friend[] }>('/friends/list');
    return response.data;
  },

  getFriendsWithGroups: async (): Promise<{ friends: any[] }> => {
    const response = await api.get<{ friends: any[] }>('/friends/list-with-groups');
    return response.data;
  },

  getFriendsLocations: async (): Promise<{ locations: FriendLocation[] }> => {
    const response = await api.get<{ locations: FriendLocation[] }>('/friends/locations');
    return response.data;
  },

  getFriendSuggestions: async (limit?: number): Promise<{ suggestions: any[]; count: number; message: string }> => {
    const url = limit ? `/friends/suggestions?limit=${limit}` : '/friends/suggestions';
    const response = await api.get<{ suggestions: any[]; count: number; message: string }>(url);
    return response.data;
  },

  getFriendSuggestionsCount: async (): Promise<{ count: number }> => {
    const response = await api.get<{ count: number }>('/friends/suggestions/count');
    return response.data;
  },

};

// Friend Groups API
export const friendGroupsAPI = {
  create: async (data: CreateFriendGroupData): Promise<{ message: string; group: FriendGroup }> => {
    const response = await api.post<{ message: string; group: FriendGroup }>('/friend-groups', data);
    return response.data;
  },

  getUserGroups: async (): Promise<{ groups: FriendGroup[] }> => {
    const response = await api.get<{ groups: FriendGroup[] }>('/friend-groups');
    return response.data;
  },

  getUserGroupsWithMembers: async (): Promise<{ groups: FriendGroupWithMembers[] }> => {
    const response = await api.get<{ groups: FriendGroupWithMembers[] }>('/friend-groups/with-members');
    return response.data;
  },

  getGroupWithMembers: async (groupId: number): Promise<{ group: FriendGroupWithMembers }> => {
    const response = await api.get<{ group: FriendGroupWithMembers }>(`/friend-groups/${groupId}`);
    return response.data;
  },

  update: async (
    groupId: number,
    data: Partial<CreateFriendGroupData>
  ): Promise<{ message: string; group: FriendGroup }> => {
    const response = await api.put<{ message: string; group: FriendGroup }>(`/friend-groups/${groupId}`, data);
    return response.data;
  },

  delete: async (groupId: number): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/friend-groups/${groupId}`);
    return response.data;
  },

  addMembers: async (groupId: number, data: AddToGroupData): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(`/friend-groups/${groupId}/members`, data);
    return response.data;
  },

  removeMember: async (groupId: number, friendId: number): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/friend-groups/${groupId}/members/${friendId}`);
    return response.data;
  },

  getGroupsForFriend: async (friendId: number): Promise<{ groups: FriendGroup[] }> => {
    const response = await api.get<{ groups: FriendGroup[] }>(`/friend-groups/friend/${friendId}`);
    return response.data;
  },
};

// Temporary Locations API
export const temporaryLocationsAPI = {
  create: async (data: CreateTemporaryLocationData): Promise<{ message: string; temporaryLocation: TemporaryLocation }> => {
    const response = await api.post<{ message: string; temporaryLocation: TemporaryLocation }>('/temporary-locations', data);
    return response.data;
  },

  getUserTemporaryLocations: async (): Promise<{ temporaryLocations: TemporaryLocation[] }> => {
    const response = await api.get<{ temporaryLocations: TemporaryLocation[] }>('/temporary-locations');
    return response.data;
  },

  getActive: async (): Promise<{ temporaryLocations: TemporaryLocation[] }> => {
    const response = await api.get<{ temporaryLocations: TemporaryLocation[] }>('/temporary-locations/active');
    return response.data;
  },

  getFriendsActive: async (): Promise<{ temporaryLocations: TemporaryLocationWithFriend[] }> => {
    const response = await api.get<{ temporaryLocations: TemporaryLocationWithFriend[] }>('/temporary-locations/friends/active');
    return response.data;
  },

  getFriendsActiveDelta: async (sinceTimestamp: number): Promise<{ temporaryLocations: TemporaryLocationWithFriend[] }> => {
    const response = await api.get<{ temporaryLocations: TemporaryLocationWithFriend[] }>(`/temporary-locations/friends/active/delta?since=${sinceTimestamp}`);
    return response.data;
  },

  getFriendTemporaryLocations: async (friendId: number): Promise<{ temporaryLocations: TemporaryLocation[] }> => {
    const response = await api.get<{ temporaryLocations: TemporaryLocation[] }>(`/temporary-locations/friends/${friendId}`);
    return response.data;
  },

  getById: async (id: number): Promise<{ temporaryLocation: TemporaryLocation }> => {
    const response = await api.get<{ temporaryLocation: TemporaryLocation }>(`/temporary-locations/${id}`);
    return response.data;
  },

  update: async (
    id: number,
    data: UpdateTemporaryLocationData
  ): Promise<{ message: string; temporaryLocation: TemporaryLocation }> => {
    const response = await api.put<{ message: string; temporaryLocation: TemporaryLocation }>(
      `/temporary-locations/${id}`,
      data
    );
    return response.data;
  },

  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/temporary-locations/${id}`);
    return response.data;
  },
};

// Notifications API
export const notificationsAPI = {
  getUnreadCount: async (): Promise<{ count: number }> => {
    const response = await api.get<{ count: number }>('/notifications/unread-count');
    return response.data;
  },

  getAll: async (limit?: number): Promise<{ notifications: any[]; count: number }> => {
    const url = limit ? `/notifications?limit=${limit}` : '/notifications';
    const response = await api.get<{ notifications: any[]; count: number }>(url);
    return response.data;
  },

  markAsRead: async (notificationId: number): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(`/notifications/${notificationId}/read`);
    return response.data;
  },

  markAllAsRead: async (): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>('/notifications/read-all');
    return response.data;
  },

  deleteNotification: async (notificationId: number): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/notifications/${notificationId}`);
    return response.data;
  },

  deleteAllRead: async (): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>('/notifications/read');
    return response.data;
  },

  // Notification preferences
  getPreferences: async (): Promise<{ preferences: any[] }> => {
    const response = await api.get<{ preferences: any[] }>('/notifications/preferences');
    return response.data;
  },

  getPreferenceByType: async (type: string): Promise<{ preference: any }> => {
    const response = await api.get<{ preference: any }>(`/notifications/preferences/${type}`);
    return response.data;
  },

  upsertPreference: async (type: string, data: any): Promise<{ message: string; preference: any }> => {
    const response = await api.put<{ message: string; preference: any }>(`/notifications/preferences/${type}`, data);
    return response.data;
  },

  // Global notification settings
  getGlobalSettings: async (): Promise<{ settings: any }> => {
    const response = await api.get<{ settings: any }>('/notifications/settings');
    return response.data;
  },

  updateGlobalSettings: async (data: any): Promise<{ message: string; settings: any }> => {
    const response = await api.put<{ message: string; settings: any }>('/notifications/settings', data);
    return response.data;
  },
};

// Friend Notes API
export const friendNotesAPI = {
  getNote: async (friendId: number): Promise<{ note: string | null }> => {
    const response = await api.get<{ note: string | null }>(`/friend-notes/${friendId}`);
    return response.data;
  },

  saveNote: async (friendId: number, note: string | null): Promise<{ message: string; note: string | null }> => {
    const response = await api.put<{ message: string; note: string | null }>(`/friend-notes/${friendId}`, { note });
    return response.data;
  },

  deleteNote: async (friendId: number): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/friend-notes/${friendId}`);
    return response.data;
  },
};

// Bug Reports API
export const bugReportsAPI = {
  create: async (description: string, pageUrl?: string, browserInfo?: string, screenshot?: Blob): Promise<{ message: string; bugReport: any }> => {
    // If screenshot exists, use FormData for multipart upload
    if (screenshot) {
      const formData = new FormData();
      formData.append('description', description);
      if (pageUrl) formData.append('page_url', pageUrl);
      if (browserInfo) formData.append('browser_info', browserInfo);
      formData.append('screenshot', screenshot, 'screenshot.png');

      const response = await api.post<{ message: string; bugReport: any }>('/bug-reports', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    }

    // No screenshot, use JSON
    const response = await api.post<{ message: string; bugReport: any }>('/bug-reports', {
      description,
      page_url: pageUrl,
      browser_info: browserInfo,
    });
    return response.data;
  },

  getMyReports: async (): Promise<any[]> => {
    const response = await api.get<any[]>('/bug-reports/my-reports');
    return response.data;
  },

  getAll: async (status?: string): Promise<any[]> => {
    const url = status ? `/bug-reports?status=${status}` : '/bug-reports';
    const response = await api.get<any[]>(url);
    return response.data;
  },

  getStats: async (): Promise<any> => {
    const response = await api.get<any>('/bug-reports/stats');
    return response.data;
  },

  updateStatus: async (id: number, status: string, adminNotes?: string): Promise<{ message: string; bugReport: any }> => {
    const response = await api.patch<{ message: string; bugReport: any }>(`/bug-reports/${id}/status`, {
      status,
      admin_notes: adminNotes,
    });
    return response.data;
  },

  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/bug-reports/${id}`);
    return response.data;
  },
};

// ============================================
// PROXIMITY API
// ============================================

export const proximityAPI = {
  /**
   * Check if user is near any friends and notify them
   * Call this when user opens app or updates their location
   */
  checkProximity: async (latitude: number, longitude: number): Promise<{ message: string; nearbyFriends: number; notificationsSent: number }> => {
    const response = await api.post<{ message: string; nearbyFriends: number; notificationsSent: number }>('/proximity/check', {
      latitude,
      longitude,
    });
    return response.data;
  },

  /**
   * Get list of nearby friends without sending notifications
   * Useful for displaying "Who's nearby" UI
   */
  getNearbyFriends: async (latitude: number, longitude: number): Promise<{ nearbyFriends: Array<{ friendId: number; distance: number; firstName: string; lastName: string }>; count: number }> => {
    const response = await api.post<{ nearbyFriends: Array<{ friendId: number; distance: number; firstName: string; lastName: string }>; count: number }>('/proximity/nearby', {
      latitude,
      longitude,
    });
    return response.data;
  },
};

// ============================================
// VISIBILITY API
// ============================================

export interface VisibilitySettings {
  default_visibility_level: number;
  description: string;
  group_rules?: Array<{
    group_id: number;
    group_name: string;
    visibility_level: number;
  }>;
  individual_rules?: Array<{
    friend_id: number;
    friend_name: string;
    visibility_level: number;
  }>;
}

export const visibilityAPI = {
  /**
   * Get visibility settings for an address
   * addressType: 'permanent' or 'temporary'
   */
  getSettings: async (addressType: 'permanent' | 'temporary', addressId: number): Promise<VisibilitySettings> => {
    const response = await api.get<VisibilitySettings>(`/visibility/${addressType}/${addressId}`);
    return response.data;
  },

  /**
   * Update default visibility level for an address
   * Level: 1=City, 2=District, 3=Street (no number), 4=Full address, 5=Full+Access
   */
  updateDefaultLevel: async (addressType: 'permanent' | 'temporary', addressId: number, level: number): Promise<{ message: string; default_visibility_level: number; description: string }> => {
    const response = await api.put<{ message: string; default_visibility_level: number; description: string }>(
      `/visibility/${addressType}/${addressId}/default`,
      { default_visibility_level: level }
    );
    return response.data;
  },

  /**
   * Set visibility rule for a specific group
   */
  setGroupVisibility: async (addressType: 'permanent' | 'temporary', addressId: number, groupId: number, level: number): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(
      `/visibility/${addressType}/${addressId}/groups/${groupId}`,
      { visibility_level: level }
    );
    return response.data;
  },

  /**
   * Delete visibility rule for a group (revert to default)
   */
  deleteGroupVisibility: async (addressType: 'permanent' | 'temporary', addressId: number, groupId: number): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(
      `/visibility/${addressType}/${addressId}/groups/${groupId}`
    );
    return response.data;
  },

  /**
   * Set visibility rule for a specific individual (friend)
   */
  setIndividualVisibility: async (addressType: 'permanent' | 'temporary', addressId: number, friendId: number, level: number): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(
      `/visibility/${addressType}/${addressId}/individuals/${friendId}`,
      { visibility_level: level }
    );
    return response.data;
  },

  /**
   * Delete visibility rule for an individual (revert to group/default)
   */
  deleteIndividualVisibility: async (addressType: 'permanent' | 'temporary', addressId: number, friendId: number): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(
      `/visibility/${addressType}/${addressId}/individuals/${friendId}`
    );
    return response.data;
  },
};

// Push Notifications API
export const pushAPI = {
  /**
   * Register a device token for push notifications
   */
  registerToken: async (token: string, platform: 'android' | 'ios' | 'web', deviceInfo?: Record<string, any>): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<{ success: boolean; message: string }>('/push/register', {
      token,
      platform,
      deviceInfo,
    });
    return response.data;
  },

  /**
   * Unregister a device token (on logout)
   */
  unregisterToken: async (token: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<{ success: boolean; message: string }>('/push/unregister', { token });
    return response.data;
  },

  /**
   * Unregister all tokens for the user (logout from all devices)
   */
  unregisterAllTokens: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<{ success: boolean; message: string }>('/push/unregister-all');
    return response.data;
  },

  /**
   * Send a test push notification
   */
  sendTest: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<{ success: boolean; message: string }>('/push/test');
    return response.data;
  },

  /**
   * Get push notification status
   */
  getStatus: async (): Promise<{ available: boolean; registeredDevices: number; firebaseConfigured: boolean }> => {
    const response = await api.get<{ available: boolean; registeredDevices: number; firebaseConfigured: boolean }>('/push/status');
    return response.data;
  },
};

// ============================================
// CONTACTS API (Phone Contact Sync)
// ============================================

export const contactsAPI = {
  /**
   * Sync hashed phone contacts with the server
   * Contacts should be hashed client-side before sending for privacy
   */
  sync: async (contacts: ContactHash[]): Promise<{ message: string; syncedCount: number; matchesCount: number }> => {
    const response = await api.post<{ message: string; syncedCount: number; matchesCount: number }>('/contacts/sync', { contacts });
    return response.data;
  },

  /**
   * Get contacts that are registered on Bob Map
   * Returns users matching the synced contact hashes
   */
  getMatches: async (): Promise<{ contacts: ContactMatch[]; count: number }> => {
    const response = await api.get<{ contacts: ContactMatch[]; count: number }>('/contacts/matches');
    return response.data;
  },

  /**
   * Get contacts not yet registered (for invitation)
   * Returns hashes that don't match any user
   */
  getNonRegistered: async (): Promise<{ contacts: Array<{ hash: string; name?: string }>; count: number }> => {
    const response = await api.get<{ contacts: Array<{ hash: string; name?: string }>; count: number }>('/contacts/non-registered');
    return response.data;
  },

  /**
   * Get sync status (whether contacts have been synced)
   */
  getStatus: async (): Promise<ContactSyncStatus> => {
    const response = await api.get<ContactSyncStatus>('/contacts/status');
    return response.data;
  },

  /**
   * Clear all synced contacts (for privacy/GDPR)
   */
  clear: async (): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>('/contacts');
    return response.data;
  },

};

// ============================================
// USER REPORTS API
// ============================================

export const userReportsAPI = {
  create: async (reportedUserId: number, reason: string, description: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/user-reports', {
      reported_user_id: reportedUserId,
      reason,
      description,
    });
    return response.data;
  },

  getAll: async (status?: string): Promise<any[]> => {
    const url = status ? `/user-reports?status=${status}` : '/user-reports';
    const response = await api.get<any[]>(url);
    return response.data;
  },

  updateStatus: async (id: number, status: string, adminNotes?: string): Promise<{ message: string }> => {
    const response = await api.patch<{ message: string }>(`/user-reports/${id}/status`, {
      status,
      admin_notes: adminNotes,
    });
    return response.data;
  },

  banUser: async (userId: number, reason?: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(`/user-reports/ban/${userId}`, { reason });
    return response.data;
  },

  deleteUserAccount: async (userId: number): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/user-reports/delete-account/${userId}`);
    return response.data;
  },
};

export default api;
